/**
 * 存储适配器契约（Storage Adapter Contract）
 * ------------------------------------------------------------
 * 整个应用只依赖这个接口，不依赖 IndexedDB 本身。
 * 未来升级云端数据库时，只需新增一个实现（如 SupabaseAdapter / RestAdapter），
 * 在 src/core/storage/index.js 里替换默认实现，UI 与业务代码零改动。
 *
 * 每个方法都返回 Promise。
 *
 * @typedef {Object} Collection
 * 集合名（见 src/core/schema.js 的 COLLECTIONS）
 *
 * @typedef {Object} StorageAdapter
 * @property {string} kind                适配器类型标识，如 'indexeddb' | 'memory' | 'rest'
 * @property {(name:Collection)=>Promise<any[]>} list          读取整表
 * @property {(name:Collection, key:string)=>Promise<any|null>} get  按主键读取
 * @property {(name:Collection, value:object)=>Promise<any>} put    按主键 upsert
 * @property {(name:Collection, values:object[])=>Promise<number>} bulkPut
 * @property {(name:Collection, key:string)=>Promise<void>} remove
 * @property {(name:Collection, keys:string[])=>Promise<number>} bulkRemove
 * @property {(name:Collection)=>Promise<void>} clear
 * @property {(name:Collection)=>Promise<number>} count
 * @property {(name:Collection, index:string, value:any)=>Promise<any[]>} query 按索引等值查询
 * @property {()=>Promise<void>} close
 */

export const REQUIRED_METHODS = [
  'list', 'get', 'put', 'bulkPut', 'remove', 'bulkRemove', 'clear', 'count', 'query',
];

/** 开发期断言：确保适配器实现了完整契约，避免换后端时出现隐性缺方法 */
export function assertAdapter(adapter) {
  const missing = REQUIRED_METHODS.filter(m => typeof adapter[m] !== 'function');
  if (missing.length) {
    throw new Error(`[storage] 适配器缺少方法: ${missing.join(', ')}`);
  }
  return adapter;
}
